/**
 * preload.ts
 * ----------------
 * Action creators for preloading data into the current app.
 */

import { payloadActionCreator } from './actionCreators';
import {
    PreloadActiveLiveSiteEvents, PreloadError, PreloadPendingApprovals, PreloadRecentUpdates,
    PreloadResolvedLiveSiteEvents, PreloadServices, PreloadStatuses, PreloadLiveSiteEvent,
} from '../constants/actions';
import { ErrorResponse } from '../models/common';
import {
    PreloadActiveLiveSiteEventsPayload, PreloadLiveSiteEventPayload, PreloadPendingApprovalsPayload,
    PreloadRecentUpdatesPayload, PreloadResolvedLiveSiteEventsPayload, PreloadServicesPayload, PreloadStatusesPayload,
} from '../models/payloads/preload';

/**
 * Creates error preloading actions.
 * @param payload Error response to preload.
 * @returns Action containing error response.
 */
export const preloadError = payloadActionCreator<ErrorResponse>(PreloadError);

/**
 * Creates pending approvals preloading actions.
 * @param payload Recent updates to preload.
 * @returns Action containing pending approvals.
 */
export const preloadPendingApprovals = payloadActionCreator<PreloadPendingApprovalsPayload>(PreloadPendingApprovals);

/**
 * Creates recent updates preloading actions.
 * @param payload Recent updates to preload.
 * @returns Action containing recent updates.
 */
export const preloadRecentUpdates = payloadActionCreator<PreloadRecentUpdatesPayload>(PreloadRecentUpdates);

/**
 * Creates service metadata preloading actions.
 * @param payload Service metadata to preload.
 * @returns Action containing service metadata.
 */
export const preloadServices = payloadActionCreator<PreloadServicesPayload>(PreloadServices);

/**
 * Creates status preloading actions.
 * @param payload Status data to preload.
 * @returns Action containing status.
 */
export const preloadStatuses = payloadActionCreator<PreloadStatusesPayload>(PreloadStatuses);

/**
 * Creates an action for preloading the list of active live site events.
 * @param payload livesite event data to preload.
 * @returns Action containing live site events.
 */
export const preloadActiveLiveSiteEvents =
    payloadActionCreator<PreloadActiveLiveSiteEventsPayload>(PreloadActiveLiveSiteEvents);

/**
 * Creates an action for preloading the selected live site event.
 * @param payload Preloaded live site event.
 * @returns Action containing active live site event.
 */
export const preloadLiveSiteEvent = payloadActionCreator<PreloadLiveSiteEventPayload>(PreloadLiveSiteEvent);

/**
 * Creates an action for preloading the list of resolved live site events.
 * @param payload livesite event data to preload.
 * @returns Action containing live site events.
 */
export const preloadResolvedLiveSiteEvents =
    payloadActionCreator<PreloadResolvedLiveSiteEventsPayload>(PreloadResolvedLiveSiteEvents);
